import React from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';

import api from '../services/api';
import { getFingerprint } from '../services/fingerprintService';
import LoadingPage from './LoadingPage';

let AuthContext = React.createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = React.useState();
  const [perms, setPerm] = React.useState([]);
  const [name, setName] = React.useState();
  const [loading, setLoading] = React.useState(true);

  // Estados para 2FA
  const [pending2FA, setPending2FA] = React.useState(null);

  const navigate = useNavigate();

  React.useEffect(() => {
    // Valida sessao chamando /me/ — cookie enviado automaticamente
    getUser();
  }, []);

  function getUser(useLoading = false) {
    if (useLoading) {
      setLoading(true);
    }
    api
      .get('/login/me/')
      .then((res) => {
        setUser(res.data.username);
        setName(res.data.name);
        setPerm(res.data.permissions);
      })
      .catch((err) => {
        setUser(null);
        setName(null);
        setPerm([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const checkPerm = (p) => perms.filter((perm) => perm.description === p);
  const hasPerm = (p) => checkPerm(p).length > 0;

  const signin = async (cred, callback, clerr) => {
    try {
      // Obtem fingerprint do navegador
      const fingerprint = await getFingerprint();

      const response = await api.post('/login/token/', {
        ...cred,
        fingerprint: fingerprint,
      });

      const data = response.data;

      // Verifica se precisa de 2FA
      if (data.requires_2fa) {
        setPending2FA({
          pending_token: data.pending_token,
          masked_email: data.masked_email,
        });
        navigate('/login/2fa');
        return;
      }

      // Login direto — cookie setado pelo servidor via Set-Cookie
      getUser(true);
      callback();
    } catch (err) {
      clerr(err?.response?.data?.detail || 'Credenciais invalidas.');
    }
  };

  const verify2FA = async (code, trustDevice, deviceName, callback, clerr) => {
    if (!pending2FA) {
      clerr('Sessao expirada. Faca login novamente.');
      navigate('/login');
      return;
    }

    try {
      await api.post('/login/2fa/verify/', {
        pending_token: pending2FA.pending_token,
        code: code,
        trust_device: trustDevice,
        device_name: deviceName || null,
      });

      // Login completo — cookie setado pelo servidor via Set-Cookie
      setPending2FA(null);
      getUser(true);
      callback();
    } catch (err) {
      clerr(err?.response?.data?.detail || 'Codigo invalido.');
    }
  };

  const resend2FACode = async (callback, clerr) => {
    if (!pending2FA) {
      clerr('Sessao expirada. Faca login novamente.');
      return;
    }

    try {
      await api.post('/login/2fa/resend/', {
        pending_token: pending2FA.pending_token,
      });
      callback();
    } catch (err) {
      clerr(err?.response?.data?.detail || 'Erro ao reenviar codigo.');
    }
  };

  const signout = async (callback = () => {}) => {
    try {
      // Invalida token no backend — servidor deleta cookie
      await api.post('/login/logout/');
    } catch (err) {
      // Ignora erro - continua com logout local
    }

    setUser(null);
    setName(null);
    setPerm([]);
    setPending2FA(null);
    navigate('/login');
    callback();
  };

  const signoutAll = async (callback = () => {}) => {
    try {
      await api.post('/login/logout/all/');
    } catch (err) {
      // Ignora erro
    }

    setUser(null);
    setName(null);
    setPerm([]);
    setPending2FA(null);
    navigate('/login');
    callback();
  };

  const cancel2FA = () => {
    setPending2FA(null);
    navigate('/login');
  };

  let value = {
    user,
    name,
    signin,
    signout,
    signoutAll,
    hasPerm,
    // 2FA
    pending2FA,
    verify2FA,
    resend2FACode,
    cancel2FA,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <LoadingPage loading={loading} /> : children}
    </AuthContext.Provider>
  );
}

export function RequireAuth({ service, children }) {
  let auth = useAuth();

  let location = useLocation();
  if (auth?.user === undefined) {
    return null;
  }

  if (!auth.user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

export function useAuth() {
  return React.useContext(AuthContext);
}

export function AuthStatus() {
  let auth = useAuth();
  let navigate = useNavigate();

  if (!auth.user) {
    return <p>You are not logged in.</p>;
  }

  return (
    <p>
      Welcome {auth.user}!{' '}
      <button
        onClick={() => {
          auth.signout(() => navigate('/'));
        }}
      >
        Sign out
      </button>
    </p>
  );
}
